// src/pages/championships/KnockoutBracket.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import mataMataService from '../../services/mataMataService';
import championshipService from '../../services/championshipService';
import matchService from '../../services/matchService';
import useAuth from '../../hooks/useAuth';
import Confetti from 'react-confetti';
import mataMataBackground from '../../assets/mata.png';
import KnockoutPairingTool from './KnockoutPairingTool';
import KnockoutDrawAnimation from './KnockoutDrawAnimation';
import './KnockoutBracket.css';

// Componentes de estado (Loading, Error, Empty)
const LoadingState = ({ children }) => (
  <div className="knockout-loading">
    {children || "Carregando chaveamento..."}
  </div>
);

const ErrorState = ({ error }) => (
  <div className="knockout-error">
    Erro ao carregar chaveamento: {error || "Tente novamente mais tarde."}
  </div>
);

const EmptyState = ({ children }) => (
  <div className="knockout-empty">
    {children || "Chaveamento ainda não disponível ou não gerado."}
  </div>
);

// MatchBox Component
function MatchBox({ match, championshipId, isFinal = false, canManage = false, onDeleteMatch }) {
  if (!match || typeof match !== 'object' || !match._id) {
    const placeholderName = typeof match === 'string' ? match : "Confronto a Definir";
    return (
      <div className="match-box match-box-placeholder match-box-empty">
        {placeholderName}
      </div>
    );
  }

  const { _id, teamA, teamB, scoreA, scoreB, status, date } = match;
  const isTeamAPopulated = teamA && typeof teamA === 'object' && teamA.name && teamA._id;
  const isTeamBPopulated = teamB && typeof teamB === 'object' && teamB.name && teamB._id;
  const teamAName = isTeamAPopulated ? teamA.name : (typeof teamA === 'string' ? teamA : "A Definir");
  const teamBName = isTeamBPopulated ? teamB.name : (typeof teamB === 'string' ? teamB : "A Definir");
  const teamABadge = isTeamAPopulated ? teamA.badge : null;
  const teamBBadge = isTeamBPopulated ? teamB.badge : null;
  const teamAImageUrl = teamABadge || '/default-team-logo.png';
  const teamBImageUrl = teamBBadge || '/default-team-logo.png';

  const handleImageError = (e) => {
    e.target.src = '/default-team-logo.png';
    e.target.onerror = null;
  };

  const isCompleted = status === 'completed';
  const isTeamAWinner = isCompleted && (scoreA !== null && scoreB !== null) && scoreA > scoreB;
  const isTeamBWinner = isCompleted && (scoreA !== null && scoreB !== null) && scoreB > scoreA;

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeleteMatch) {
      onDeleteMatch(_id);
    }
  };

  return (
    <Link to={`/championships/${championshipId}/matches/${_id}`} className="match-box-link">
      <div className={`match-box status-${status || 'scheduled'} ${isFinal ? 'final-match' : ''}`}>
        <div className={`match-team team-a ${isTeamAWinner ? 'match-winner' : ''} ${!isTeamAPopulated ? 'team-placeholder' : ''}`}>
          <div className="team-logo-wrapper">
            {isTeamAPopulated ? (
              <img
                src={teamAImageUrl}
                alt={teamAName}
                onError={handleImageError}
                className="team-logo-img"
              />
            ) : (
              <div className="logo-placeholder">?</div>
            )}
          </div>
          <span className="team-name">
            {teamAName}
            {isTeamAWinner && isFinal && (
              <span className="crown-icon" title="Campeão">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="16" height="16">
                  <path fill="gold" d="M576 136c0 22.09-17.91 40-40 40h-64.81L359.1 32h-144L104.8 176H40c-22.09 0-40-17.91-40-40s17.91-40 40-40h92.13l92.66-92.66c4.75-4.75 12.44-4.75 17.19 0L443.87 96H536c22.09 0 40 17.91 40 40zM448 224l-50.91 50.91c-18.75 18.74-49.13 18.74-67.88 0L288 160l-41.22 114.91c-6.25 16.72-22.19 27.09-39.97 27.09H96v128h384V224z"/>
                </svg>
              </span>
            )}
          </span>
          {isCompleted && typeof scoreA === 'number' && (
            <span className="team-score">{scoreA}</span>
          )}
        </div>

        <div className="match-vs">vs</div>

        <div className={`match-team team-b ${isTeamBWinner ? 'match-winner' : ''} ${!isTeamBPopulated ? 'team-placeholder' : ''}`}>
          <div className="team-logo-wrapper">
            {isTeamBPopulated ? (
              <img
                src={teamBImageUrl}
                alt={teamBName}
                onError={handleImageError}
                className="team-logo-img"
              />
            ) : (
              <div className="logo-placeholder">?</div>
            )}
          </div>
          <span className="team-name">
            {teamBName}
            {isTeamBWinner && isFinal && (
              <span className="crown-icon" title="Campeão">
                <svg xmlns="http://www.w3.org2000/svg" viewBox="0 0 576 512" width="16" height="16">
                  <path fill="gold" d="M576 136c0 22.09-17.91 40-40 40h-64.81L359.1 32h-144L104.8 176H40c-22.09 0-40-17.91-40-40s17.91-40 40-40h92.13l92.66-92.66c4.75-4.75 12.44-4.75 17.19 0L443.87 96H536c22.09 0 40 17.91 40 40zM448 224l-50.91 50.91c-18.75 18.74-49.13 18.74-67.88 0L288 160l-41.22 114.91c-6.25 16.72-22.19 27.09-39.97 27.09H96v128h384V224z"/>
                </svg>
              </span>
            )}
          </span>
          {isCompleted && typeof scoreB === 'number' && (
            <span className="team-score">{scoreB}</span>
          )}
        </div>

        {date && (
          <div className="match-date-scheduled">
            {new Date(date).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit'
            })} - {new Date(date).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}

        {/* BOTÕES DE AÇÃO PARA ADMINISTRADORES */}
        {canManage && (
          <div className="match-actions-overlay">
            <button 
              onClick={handleDeleteClick}
              className="delete-match-btn"
              title="Excluir partida"
            >
              🗑️
            </button>
          </div>
        )}
      </div>
    </Link>
  );
}

// Componente para exibir o pódio completo
const PodiumDisplay = ({ rankings }) => {
  if (!rankings.champion && !rankings.runnerUp && !rankings.thirdPlace) {
    return null;
  }
  
  return (
    <div className="podium-container">
      <h3>🏆 PÓDIO DO CAMPEONATO 🏆</h3>
      <div className="podium-grid">
        {/* 2º lugar */}
        <div className="podium-place second-place">
          <div className="podium-medal">🥈</div>
          <div className="podium-team">
            {rankings.runnerUp?.name || "A definir"}
          </div>
          <div className="podium-label">VICE-CAMPEÃO</div>
        </div>
        
        {/* 1º lugar */}
        <div className="podium-place first-place">
          <div className="podium-medal">🥇</div>
          <div className="podium-team champion-team">
            {rankings.champion?.name || "A definir"}
          </div>
          <div className="podium-label">CAMPEÃO</div>
        </div>
        
        {/* 3º lugar */}
        <div className="podium-place third-place">
          <div className="podium-medal">🥉</div>
          <div className="podium-team">
            {rankings.thirdPlace?.name || "A definir"}
          </div>
          <div className="podium-label">3º LUGAR</div>
        </div>
      </div>
    </div>
  );
};

// Componente principal KnockoutBracket
function KnockoutBracket() {
  const { id } = useParams();
  const { user } = useAuth();
  const [bracketData, setBracketData] = useState(null);
  const [championship, setChampionship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [champion, setChampion] = useState(null);
  const [showPairingTool, setShowPairingTool] = useState(false);
  const [deletingMatch, setDeletingMatch] = useState(false);
  
  // ESTADOS PARA OS COLOCADOS
  const [rankings, setRankings] = useState({
    champion: null,
    runnerUp: null,
    thirdPlace: null
  });
  
  // NOVOS ESTADOS
  const [showDrawAnimation, setShowDrawAnimation] = useState(false);
  const [teamsForDraw, setTeamsForDraw] = useState([]);
  const [nextPhaseToGenerate, setNextPhaseToGenerate] = useState(null);
  const [visualBracket, setVisualBracket] = useState(null);

  const isChampionshipManager = useCallback(() => {
    if (!user || !championship) return false;
    const userId = user._id;
    const isCreator = championship.creator?._id === userId;
    const isManager = championship.managers?.some(m => m._id === userId);
    return isCreator || isManager;
  }, [user, championship]);

  const canGenerateBracket = user?.role &&
    (user.role.toLowerCase() === 'admin' ||
    (user.role.toLowerCase() === 'gestor' && isChampionshipManager()));

  // FUNÇÃO PARA CALCULAR TODOS OS RANKINGS
  const calculateRankings = useCallback((bracketData) => {
    if (!bracketData) {
      return {
        champion: null,
        runnerUp: null,
        thirdPlace: null
      };
    }
    
    const newRankings = {
      champion: null,
      runnerUp: null,
      thirdPlace: null
    };
    
    // Campeão e Vice-campeão
    if (bracketData.final?.status === 'completed') {
      const { scoreA, scoreB, teamA, teamB } = bracketData.final;
      if (typeof scoreA === 'number' && typeof scoreB === 'number') {
        newRankings.champion = scoreA > scoreB ? teamA : teamB;
        newRankings.runnerUp = scoreA > scoreB ? teamB : teamA;
      }
    }
    
    // Terceiro lugar
    if (bracketData.thirdPlace?.status === 'completed') {
      const { scoreA, scoreB, teamA, teamB } = bracketData.thirdPlace;
      if (typeof scoreA === 'number' && typeof scoreB === 'number') {
        newRankings.thirdPlace = scoreA > scoreB ? teamA : teamB;
      }
    }
    
    return newRankings;
  }, []);

  // NOVA FUNÇÃO: Gera a estrutura de placeholders
  const generateBracketStructure = useCallback((r16, qf, sf, f) => {
    const createPlaceholders = (count, prefix) => 
      Array.from({ length: count }, (_, i) => `${prefix} ${i + 1}`);
    
    return {
      roundOf16: createPlaceholders(r16, "Oitavas"),
      quarterFinals: createPlaceholders(qf, "Quartas"),
      semiFinals: createPlaceholders(sf, "Semi"),
      final: createPlaceholders(f, "Final"),
      thirdPlace: createPlaceholders(f > 0 ? 1 : 0, "3º Lugar"),
    };
  }, []);

  // NOVA FUNÇÃO: Mescla dados reais com placeholders
  const mergeMatches = useCallback((placeholders, realMatches, previousRoundMatches = [], prevRoundName = '', forThirdPlace = false) => {
    const real = Array.isArray(realMatches) ? realMatches : (realMatches ? [realMatches] : []);
    const prev = Array.isArray(previousRoundMatches) ? previousRoundMatches : [];
    
    const merged = placeholders.map((_, index) => {
      // Se já existe uma partida real para esta posição, use-a
      if (real[index]) {
        return real[index];
      }

      // Se não, cria um placeholder dinâmico
      if (forThirdPlace) {
        const loser1 = `Perdedor ${prevRoundName} 1`;
        const loser2 = `Perdedor ${prevRoundName} 2`;
        return { _id: `ph-3rd-${index}`, teamA: loser1, teamB: loser2 };
      } else {
        const winner1MatchIndex = index * 2;
        const winner2MatchIndex = index * 2 + 1;
        
        // Busca times reais das partidas anteriores se já estiverem concluídas
        const prevMatch1 = prev[winner1MatchIndex];
        const prevMatch2 = prev[winner2MatchIndex];
        
        let teamA_placeholder = `Vencedor ${prevRoundName} ${winner1MatchIndex + 1}`;
        let teamB_placeholder = `Vencedor ${prevRoundName} ${winner2MatchIndex + 1}`;
        
        // Se a partida anterior já tem vencedor, usa o time real
        if (prevMatch1?.status === 'completed' && prevMatch1?.winningTeam) {
          teamA_placeholder = prevMatch1.winningTeam;
        }
        
        if (prevMatch2?.status === 'completed' && prevMatch2?.winningTeam) {
          teamB_placeholder = prevMatch2.winningTeam;
        }

        return { 
          _id: `ph-${prevRoundName}-${index}`, 
          teamA: teamA_placeholder, 
          teamB: teamB_placeholder,
          status: 'scheduled'
        };
      }
    });

    return merged.length === 1 && !Array.isArray(realMatches) ? merged[0] : merged;
  }, []);

  // NOVA FUNÇÃO: Constrói o bracket visual completo
  const buildVisualBracket = useCallback((realData, champData) => {
    if (!champData || !realData) {
      setVisualBracket(null);
      return;
    }

    const format = champData.format || '';
    let structure = {};

    // Define a estrutura baseada no formato do campeonato
    if (format.includes('16 times') || format.includes('32 times')) { // Formatos com Oitavas
      structure = generateBracketStructure(8, 4, 2, 1); // 8 oitavas, 4 quartas, 2 semis, 1 final
    } else if (format.includes('8 times')) { // Formatos com Quartas
      structure = generateBracketStructure(0, 4, 2, 1);
    } else if (format.includes('4 times')) { // Formatos com Semis
      structure = generateBracketStructure(0, 0, 2, 1);
    } else {
      // Fallback para formato desconhecido
      setVisualBracket(realData);
      return;
    }

    // Preenche a estrutura com os dados reais da API
    const finalBracket = {
      roundOf16: mergeMatches(structure.roundOf16, realData.roundOf16),
      quarterFinals: mergeMatches(structure.quarterFinals, realData.quarterFinals, realData.roundOf16, "Oitavas"),
      semiFinals: mergeMatches(structure.semiFinals, realData.semiFinals, realData.quarterFinals, "Quartas"),
      final: mergeMatches(structure.final, realData.final, realData.semiFinals, "Semi"),
      thirdPlace: mergeMatches(structure.thirdPlace, realData.thirdPlace, realData.semiFinals, "Semi", true),
    };
    
    setVisualBracket(finalBracket);
  }, [generateBracketStructure, mergeMatches]);

  const fetchBracketAndChampionship = useCallback(async () => {
    if (!id) {
      setError("ID do campeonato não fornecido.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [bracketResponse, champResponse] = await Promise.all([
        mataMataService.getKnockoutBracket(id),
        championshipService.getById(id)
      ]);

      const realBracketData = bracketResponse.data || {};
      const champData = champResponse.data || null;

      setBracketData(realBracketData);
      setChampionship(champData);

      // Constrói o bracket visual com placeholders
      buildVisualBracket(realBracketData, champData);

      // Calcula todos os rankings
      const newRankings = calculateRankings(realBracketData);
      setRankings(newRankings);

      // Lógica de celebração do campeão
      if (newRankings.champion) {
        setShowCelebration(true);
        setChampion(newRankings.champion);
      } else {
        setShowCelebration(false);
        setChampion(null);
      }
    } catch (err) {
      setError(err.message || "Erro ao buscar chaveamento.");
      setBracketData({});
    } finally {
      setLoading(false);
    }
  }, [id, buildVisualBracket, calculateRankings]);

  useEffect(() => {
    fetchBracketAndChampionship();
  }, [fetchBracketAndChampionship]);

  // FUNÇÃO PARA EXCLUIR PARTIDA DE MATA-MATA
  const handleDeleteMatch = async (matchId) => {
    if (!canGenerateBracket) {
      alert('Você não tem permissão para excluir partidas.');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir esta partida? Esta ação não pode ser desfeita.')) {
      return;
    }

    setDeletingMatch(true);
    try {
      await matchService.delete(matchId);
      alert('Partida excluída com sucesso!');
      
      // Recarrega os dados para atualizar a visualização
      await fetchBracketAndChampionship();
    } catch (err) {
      console.error('Erro ao excluir partida:', err);
      alert('Erro ao excluir a partida. Tente novamente.');
    } finally {
      setDeletingMatch(false);
    }
  };

  // FUNÇÃO ATUALIZADA: AGORA ABRE A TELA DE SORTEIO
  const handleGenerateKnockout = async () => {
    if (!id || !canGenerateBracket) return;
    
    setLoading(true);
    setError(null);
    try {
      // 1. Busca os times classificados primeiro
      const response = await championshipService.getKnockoutQualifiers(id, 'initial');
      const teams = response.data.data || [];
      if (teams.length === 0) {
        throw new Error("Nenhum time classificado encontrado para iniciar o sorteio.");
      }
      
      // 2. Armazena os times e abre a tela de animação
      setTeamsForDraw(teams);
      setShowDrawAnimation(true);

    } catch (err) {
      setError(err.message || "Erro ao buscar classificados para o sorteio.");
    } finally {
      setLoading(false);
    }
  };

  // Funções de controle para o sorteio
  const handleDrawComplete = () => {
    setShowDrawAnimation(false);
    fetchBracketAndChampionship();
  };

  const handleDrawCancel = () => {
    setShowDrawAnimation(false);
  };

  const handlePairingToolSubmit = () => {
    setShowPairingTool(false);
    fetchBracketAndChampionship();
  };

  const handlePairingToolCancel = () => {
    setShowPairingTool(false);
  };

  // Funções auxiliares para verificar o estado das fases
  const isPhaseCompleted = useCallback((phaseMatches) => {
    if (!phaseMatches) return false;
    if (!Array.isArray(phaseMatches) && phaseMatches._id) {
      return phaseMatches.status === 'completed' && phaseMatches.winningTeam;
    }
    return Array.isArray(phaseMatches) && phaseMatches.length > 0 && 
           phaseMatches.every(m => m.status === 'completed' && m.winningTeam);
  }, []);

  const isPhaseGenerated = useCallback((phaseMatches) => {
    return (phaseMatches && (Array.isArray(phaseMatches) ? phaseMatches.length > 0 : phaseMatches?._id));
  }, []);

  // Determinar a próxima fase a ser gerada - ATUALIZADO COM SUPORTE PARA 4 TIMES
  const determineNextPhase = useCallback(() => {
    if (!bracketData || !championship) return null;
    
    const format = championship.format;
    let phasesInOrder = [];

    if (format.includes('Mata-mata (8 times)') || format.includes('Fase de grupos + Mata-mata (16 times)')) {
      phasesInOrder = ['quarterFinals', 'semiFinals', 'final', 'thirdPlace'];
    } else if (format.includes('Mata-mata (16 times)') || format.includes('Fase de grupos + Mata-mata (32 times)')) {
      phasesInOrder = ['roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace'];
    }
    // NOVA CONDIÇÃO PARA O FORMATO DE 4 TIMES
    else if (format.includes('Mata-mata (4 times)')) {
      phasesInOrder = ['semiFinals', 'final', 'thirdPlace'];
    }
    else {
      phasesInOrder = ['roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace'];
    }

    for (const phaseKey of phasesInOrder) {
      const phaseData = bracketData[phaseKey];
      if (!isPhaseGenerated(phaseData) || !isPhaseCompleted(phaseData)) {
        return phaseKey;
      }
    }
    
    return null;
  }, [bracketData, championship, isPhaseGenerated, isPhaseCompleted]);

  // Atualiza a próxima fase sempre que os dados mudarem
  useEffect(() => {
    if (bracketData && championship) {
      setNextPhaseToGenerate(determineNextPhase());
    }
  }, [bracketData, championship, determineNextPhase]);

  const handleGenerateNextPhase = () => {
    const nextPhase = determineNextPhase();
    if (nextPhase) {
      setNextPhaseToGenerate(nextPhase);
      setShowPairingTool(true);
    }
  };

  // ✅ FUNÇÃO renderRound ATUALIZADA PARA AGRUPAR PARTIDAS
  const renderRound = (matches, title, phaseKey) => {
    const arr = Array.isArray(matches) ? matches : (matches ? [matches] : []);
    if (arr.length === 0) return null;

    const isFinal = phaseKey === 'final';
    const isThirdPlace = phaseKey === 'thirdPlace';

    // Agrupa as partidas em pares
    const matchPairs = [];
    for (let i = 0; i < arr.length; i += 2) {
        matchPairs.push(arr.slice(i, i + 2));
    }

    return (
        <div className={`bracket-round ${isFinal ? 'final-round' : ''} ${isThirdPlace ? 'third-place-round' : ''}`} data-phase={phaseKey}>
            <h4 className="knockout-round-title">{title}</h4>
            <div className="matches-column">
                {/* Itera sobre os pares de partidas */}
                {matchPairs.map((pair, pairIndex) => (
                    <div className="match-pair-wrapper" key={`pair-${phaseKey}-${pairIndex}`}>
                        {pair.map((match, matchIndex) => {
                            // Verificar se este é um vencedor para destacar o caminho
                            const isWinner = match?.status === 'completed' && match?.winningTeam;
                            
                            return (
                                <div 
                                    key={match?._id || `ph-${phaseKey}-${pairIndex}-${matchIndex}`} 
                                    className={`match-wrapper ${isWinner ? 'winner-path' : ''}`}
                                >
                                    <MatchBox 
                                        match={match} 
                                        championshipId={id}
                                        isFinal={isFinal}
                                        canManage={canGenerateBracket}
                                        onDeleteMatch={handleDeleteMatch}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
  };

  // Se a tela de sorteio estiver ativa, renderiza ela
  if (showDrawAnimation) {
    return (
      <KnockoutDrawAnimation
        championshipId={id}
        teams={teamsForDraw}
        phase={nextPhaseToGenerate}
        onDrawComplete={handleDrawComplete}
        onCancel={handleDrawCancel}
      />
    );
  }

  if (loading && !bracketData) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  // Verifica se há algum bracket gerado
  const hasGeneratedBracket = isPhaseGenerated(bracketData?.roundOf16) || 
                             isPhaseGenerated(bracketData?.quarterFinals) || 
                             isPhaseGenerated(bracketData?.semiFinals) || 
                             isPhaseGenerated(bracketData?.final) || 
                             isPhaseGenerated(bracketData?.thirdPlace);

  const isManualMode = championship?.knockoutPairingMethod === 'manual';

  // Se o pairing tool está aberto, mostra ele
  if (showPairingTool && nextPhaseToGenerate) {
    return (
      <KnockoutPairingTool
        championshipId={id}
        canManage={canGenerateBracket}
        currentPhase={nextPhaseToGenerate}
        onPairingComplete={handlePairingToolSubmit}
        onCancel={handlePairingToolCancel}
      />
    );
  }

  // Se não há bracket gerado
  if (!hasGeneratedBracket) {
    if (!canGenerateBracket) {
      return <EmptyState>Chaveamento ainda não gerado pelo organizador.</EmptyState>;
    }

    switch (championship?.knockoutPairingMethod) {
      case 'standard':
      case 'random':
        return (
          <EmptyState>
            Chaveamento ({championship.knockoutPairingMethod === 'random' ? 'Sorteio Aleatório' : 'Padrão'}) ainda não gerado.
            <button onClick={handleGenerateKnockout} className="generate-knockout-button">
              Gerar Mata-Mata (Automático)
            </button>
          </EmptyState>
        );
      case 'manual':
        return (
          <EmptyState>
            Chaveamento manual ainda não gerado.
            <button onClick={handleGenerateNextPhase} className="generate-knockout-button">
              Gerar Primeira Fase
            </button>
          </EmptyState>
        );
      default:
        return <ErrorState error="Método de chaveamento não definido para este campeonato." />;
    }
  }

  // Verificar se o campeonato está finalizado
  const isChampionshipCompleted = rankings.champion && rankings.runnerUp;

  // Mostra o bracket visual em árvore
  return (
    <>
      {showCelebration && (
        <>
          <Confetti 
            numberOfPieces={300} 
            gravity={0.25} 
            recycle={false} 
          />
          <div className="champion-banner">
            🏆 CAMPEÃO: <strong>{champion?.name}</strong>
          </div>
        </>
      )}
      
      <div
        className="knockout-container"
        style={{
          backgroundImage: `url(${mataMataBackground})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <div className="knockout-header">
          <h3 className="knockout-main-title">Fase Mata-Mata</h3>
          <div className="knockout-actions">
            {canGenerateBracket && championship?.knockoutPairingMethod !== 'manual' && (
              <button 
                onClick={handleGenerateKnockout} 
                className="generate-knockout-button" 
                disabled={loading || deletingMatch}
              >
                {loading ? "Processando..." : "Gerar/Atualizar Chaves"}
              </button>
            )}
            {canGenerateBracket && championship?.knockoutPairingMethod === 'manual' && nextPhaseToGenerate && (
              <button 
                onClick={handleGenerateNextPhase} 
                className="generate-next-phase-button" 
                disabled={loading || deletingMatch}
              >
                {loading ? "Processando..." : "Gerar Próxima Fase"}
              </button>
            )}
            {/* BOTÃO PARA IR PARA GERENCIAMENTO AVANÇADO */}
            {canGenerateBracket && (
              <Link to={`/championships/${id}/manage`} className="advanced-management-btn">
                Gerenciamento Avançado
              </Link>
            )}
          </div>
        </div>

        {/* EXIBIR PÓDIO SE O CAMPEONATO ESTIVER FINALIZADO */}
        {isChampionshipCompleted && (
          <PodiumDisplay rankings={rankings} />
        )}

        {/* ESTRUTURA EM ÁRVORE COM LINHAS CONECTORAS */}
        <div className="bracket-tree">
          {visualBracket && renderRound(visualBracket.roundOf16, "Oitavas de Final", "roundOf16")}
          {visualBracket && renderRound(visualBracket.quarterFinals, "Quartas de Final", "quarterFinals")}
          {visualBracket && renderRound(visualBracket.semiFinals, "Semifinal", "semiFinals")}
        </div>
        
        {/* Final e 3º lugar em linha separada abaixo */}
        <div className="bracket-layout">
          {visualBracket && renderRound(visualBracket.final, "Final", "final")}
          {visualBracket && renderRound(visualBracket.thirdPlace, "Disputa de 3º Lugar", "thirdPlace")}
        </div>

        {/* MENSAGEM DE DELETANDO */}
        {deletingMatch && (
          <div className="deleting-overlay">
            <div className="deleting-spinner">🗑️</div>
            <p>Excluindo partida...</p>
          </div>
        )}
      </div>
    </>
  );
}

export default KnockoutBracket;